import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { toPascalCase } from 'components/WorkspaceEditor/utils'
import {
    InputBuilder,
    InputBuilderProps,
    TextContentElementMutation,
} from './InputBuilder'
import { ObjectBuilder } from './ObjectBuilder'
import {
    findMatchingFamilyProductType,
    getContentItemIds,
    isProductFamilyWithProductsPartType,
} from './builder.utils'

/** Input parameters for the mutation to update the content item element. */
interface SetProjectContentItemElementsInput {
    projectProductFamilyId: string
    contentItemId: string
    contentItemOwnerType: string
    elements: TextContentElementMutation[]
    projectId: string
    contentItemOwnerId: string
    nodeId: string
    textType: string
    partType: string
    documentTemplateViewId: string
    documentTemplateViewItemId: string
    contentItemParentId: string
    contentItemParentType: string
    propertyKey?: string
    isFromStudioManagementModule: boolean
    specifierNotes?: string[]
    isMasterSpecCloneNode: boolean
    selectedPillElementId: string
    isDeletingWholeParagraph: boolean
    isDeletingBaseContent: boolean
}

/** Variables for the mutation to update the content item element. */
interface SetProjectContentItemElementsProps {
    input: SetProjectContentItemElementsInput
    partType: string
    projectId: string
    rootArticleContentItemIds: string[]
    productFamilyId: string
    isTrackChangeToggleOn: boolean
}

export interface ContentItemInputBuilderProps extends InputBuilderProps {
    changedElements: Partial<TextContentElement>[]
    familyAddedProductTypes?: ProjectElementNode[]
    elementType?: ProjectElementType
    shouldReplaceWholeElements?: boolean
    isNewElementAfterKeyedElement?: boolean
}

/**
 * A class that encapsulates helper functions for building input variables
 * for updating project content item elements.
 * @param {ContentItemInputBuilderProps} props - The properties for the builder.
 * @param {string} props.contentItemId - The ID of the content item.
 * @param {Partial<TextContentElement>[]} props.changedElements - The elements to be changed.
 * @param {string} props.selectedProjectElementNodeId - The ID of the selected project element node.
 * @param {string} props.elementId - The ID of the element.
 * @param {ContentItemViewNodesType[]} props.contentItemViewNodes - The view nodes of the content item.
 */
export class ContentItemInputBuilder extends InputBuilder<
    SetProjectContentItemElementsInput,
    SetProjectContentItemElementsProps,
    Partial<TextContentElement>[]
> {
    familyAddedProductTypes: ProjectElementNode[]
    elementType?: ProjectElementType
    shouldReplaceWholeElements?: boolean
    isNewElementAfterKeyedElement?: boolean
    constructor({
        contentItemId,
        changedElements,
        selectedProjectElementNodeId,
        elementId,
        contentItemViewNodes,
        projectId = '',
        familyAddedProductTypes = [],
        elementType,
        shouldReplaceWholeElements = false,
        isNewElementAfterKeyedElement = false,
    }: ContentItemInputBuilderProps) {
        super(
            {
                contentItemId,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes,
                projectId,
            },
            // pass changedElements as the specific data (TData)
            changedElements
        )
        /** get the current ContentItem value */
        this.contentItem = this.getContentItem(contentItemId)
        /** use projectId from url if not already set */
        if (this.contentItem && !this.contentItem.projectId)
            this.contentItem.projectId = projectId
        /** sets the family added product types */
        this.familyAddedProductTypes = familyAddedProductTypes
        /** sets the element type if provided */
        this.elementType = elementType
        /** sets the value to replace whole elements in array */
        this.shouldReplaceWholeElements = shouldReplaceWholeElements
        this.isNewElementAfterKeyedElement = isNewElementAfterKeyedElement
    }

    /**
     * Recursively checks if an element or its nested elements contain the target ID.
     * @param element - The element to check.
     * @param targetId - The ID to search for.
     * @returns True if the element or its nested elements contain the target ID.
     */
    private hasElementWithId = (
        element: Partial<TextContentElement>,
        targetId: string
    ): boolean => {
        // Check if current element has the target ID
        if (element.id === targetId) {
            return true
        }

        // Recursively check nested elements
        if (element.elements && element.elements.length > 0) {
            return element.elements.some((nestedElement) =>
                this.hasElementWithId(nestedElement, targetId)
            )
        }

        return false
    }

    /**
     * Retrieves the content item node from global store by its ID.
     * Performs a deep search through element hierarchies to find matching IDs.
     * @param contentItemId - The ID of the content item.
     * @returns The view node containing the content item.
     */
    protected getContentItem = (contentItemId: string) =>
        this.contentItemViewNodes.find((node) =>
            node.elements?.some((element) =>
                this.hasElementWithId(element, contentItemId)
            )
        ) as ContentItemViewNodesType

    /**
     * Retrieves the root article node from content item view nodes (typically from global store).
     * Recursively searches upwards based on dtvParentId.
     * @param node - The node to start the search from.
     * @returns The root article node.
     */
    protected getRootArticle = (
        node: Partial<ContentItemViewNodesType> | undefined
    ): Partial<ContentItemViewNodesType> | undefined => {
        if (!node) {
            return undefined
        }
        if (node.textType === 'Article') {
            return node
        }
        return this.getRootArticle(
            this.contentItemViewNodes.find(
                (ci) => ci.dtvItemId === node.dtvParentId
            )
        )
    }

    /**
     * Builds the elements array for the mutation.
     * Replaces the target element within the content item's elements
     * with the changed elements and maps them to the mutation model.
     * @returns An array of TextContentElementMutation.
     */
    protected buildElements = (): TextContentElementMutation[] => {
        const updatedElements = this.shouldReplaceWholeElements
            ? this.specificData
            : this.replaceOrInsertInArray(
                  this.contentItem?.elements || [],
                  this.specificData, // (changedElements)
                  this.isNewElementAfterKeyedElement
              )

        return updatedElements.map(this.toMutationModel.bind(this))
    }

    /**
     * Determines the appropriate node ID for the mutation based on family added product types.
     * For PRODUCTS part type, attempts to find a matching family added product type
     * and returns its ID if found, otherwise returns the default selected project element node ID.
     * @returns The node ID to use for the mutation.
     */
    protected resolveNodeIdForProductType = (): string => {
        if (
            !isProductFamilyWithProductsPartType(
                this.familyAddedProductTypes,
                this.contentItem,
                this.elementType
            )
        ) {
            return this.selectedProjectElementNodeId || ''
        }

        const contentItemIds = getContentItemIds(this.contentItem)
        const matchingProductType = findMatchingFamilyProductType(
            this.familyAddedProductTypes,
            this.selectedProjectElementNodeId,
            contentItemIds
        )

        return matchingProductType?.id || this.selectedProjectElementNodeId
    }

    /**
     * Maps the content item and changed elements into a Builder instance
     * containing the input for the updateProjectContentItemElements mutation.
     *
     * @param builder - A Builder for SetProjectContentItemElementsInput.
     * @returns The builder with the input set.
     */
    protected buildInput = (
        builder: ObjectBuilder<SetProjectContentItemElementsInput>
    ): ObjectBuilder<SetProjectContentItemElementsInput> => {
        const elements = this.buildElements()
        const nodeId = this.resolveNodeIdForProductType()

        return (
            builder
                .resolveWith(this.nullOrUndefinedResolver)
                .set('projectProductFamilyId', this.elementId)
                .set('contentItemId', this.contentItem?.id)
                .set(
                    'contentItemOwnerType',
                    toPascalCase(this.contentItem?.type)
                )
                .set('elements', elements)
                .set('projectId', this.contentItem?.projectId)
                .set(
                    'contentItemOwnerId',
                    this.contentItem?.propertyRefId ||
                        this.contentItem?.dtvItemId
                )
                .set('nodeId', nodeId)
                .set('textType', this.contentItem?.textType)
                .set(
                    'partType',
                    this.contentItem?.partType?.toLocaleUpperCase() || ''
                )
                .set(
                    'documentTemplateViewId',
                    this.contentItem?.documentTemplateViewId
                )
                .set('documentTemplateViewItemId', this.contentItem?.dtvItemId)
                .set(
                    'contentItemParentId',
                    this.contentItem?.dtvContentItemParentId ||
                        this.contentItem?.dtvParentId
                )
                .set(
                    'contentItemParentType',
                    // If dtvContentItemParentId exists, parent type is ContentItem
                    // Otherwise, look up the parent type using dtvParentId
                    this.contentItem?.dtvContentItemParentId
                        ? 'ContentItem'
                        : this.getParentType(
                              this.contentItem?.dtvParentId || ''
                          )
                )
                .set('isFromStudioManagementModule', false)
                .set('isMasterSpecCloneNode', false)
                // for PT content, use 1st element id as the selected pill element id
                // otherwise use the content item id
                .set(
                    'selectedPillElementId',
                    this.contentItem?.partType?.toLocaleUpperCase() ===
                        'PRODUCTS'
                        ? elements[0].id
                        : this.contentItemId
                )
                .set('isDeletingWholeParagraph', false)
                .set('isDeletingBaseContent', !this.contentItem?.isUserAdded)
        )
    }

    /**
     * Builds the input variables for the mutation.
     * @returns The variables to be used in mutation.
     */
    build = (): SetProjectContentItemElementsProps => {
        const rootArticle = this.getRootArticle(this.contentItem)

        return new ObjectBuilder<SetProjectContentItemElementsProps>()
            .resolveWith(this.nullOrUndefinedResolver)
            .set('input', this.buildInput.bind(this))
            .set('partType', this.contentItem?.partType?.toLocaleUpperCase())
            .set('projectId', this.contentItem?.projectId)
            .set('rootArticleContentItemIds', [rootArticle?.id || ''])
            .set('productFamilyId', this.elementId)
            .set('isTrackChangeToggleOn', true)
            .build()
    }
}
